// The roster search contract (Issue 06): case-insensitive SUBSTRING matching over unit
// display names and Class labels — predictable and explainable, no fuzzy scoring. Search
// state is transient by design (ADR 0002 keeps the URL contract to sort + facets); the
// query never serializes.

/** The three fields the matcher reads — the UI builds these once from the dataset. */
export interface SearchableUnit {
  id: string;
  name: string;
  /** The unit's Class display name ("Frigate", "Capital Ship", …). */
  kindLabel: string;
}

// Rank tiers, best first: the whole name starts with the query, a word in the name starts
// with it, it appears mid-word, or only the Class label matches.
const NAME_PREFIX = 0;
const WORD_START = 1;
const NAME_SUBSTRING = 2;
const CLASS_LABEL = 3;

function escapeRegExp(text: string): string {
  return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// The tier is a property of the whole name, not of its first occurrence: "Acolyte
// Corvette" ranks as a word-start match for "co" even though a mid-word hit comes first.
function matchTier(unit: SearchableUnit, query: string): number | undefined {
  const name = unit.name.toLowerCase();
  if (name.startsWith(query)) return NAME_PREFIX;
  if (new RegExp(`[^a-z0-9]${escapeRegExp(query)}`).test(name)) return WORD_START;
  if (name.includes(query)) return NAME_SUBSTRING;
  return unit.kindLabel.toLowerCase().includes(query) ? CLASS_LABEL : undefined;
}

/** Match `query` against the units' names and Class labels: name matches rank before
 * class-label matches, earlier word positions before later, ties alphabetically. A blank
 * query matches nothing (the empty search box shows no results, not the whole roster). */
export function searchUnits<T extends SearchableUnit>(
  units: readonly T[],
  query: string,
  limit = 8,
): T[] {
  const q = query.trim().toLowerCase();
  if (q === "") return [];
  const scored: [number, T][] = [];
  for (const unit of units) {
    const tier = matchTier(unit, q);
    if (tier !== undefined) scored.push([tier, unit]);
  }
  scored.sort((a, b) => a[0] - b[0] || a[1].name.localeCompare(b[1].name));
  return scored.slice(0, limit).map(([, unit]) => unit);
}
